import React, { useCallback, useState } from 'react';
import { InputStep } from '../components/naval/InputStep';
import { LoadingStep } from '../components/naval/LoadingStep';
import { ClarificationStep } from '../components/naval/ClarificationStep';
import { OutputStep } from '../components/naval/OutputStep';
import { AlertCircleIcon, RefreshCwIcon } from 'lucide-react';
type Step = 'input' | 'loading' | 'clarification' | 'output' | 'error';
interface ClarificationQuestion {
  id: string;
  question: string;
}
interface OutputData {
  mentalModels: string[];
  reframeText: string;
  quote: {
    text: string;
    source: string;
  };
  socraticQuestions: string[];
}
// Mock data for prototype demonstration
const MOCK_CLARIFICATION_QUESTIONS: ClarificationQuestion[] = [{
  id: '1',
  question: 'What specific skills or knowledge do you have that would give you an edge in your new venture?'
}, {
  id: '2',
  question: 'What does "success" look like for you in 5 years if you stay vs. if you leave?'
}, {
  id: '3',
  question: 'Is your current unfulfillment about the work itself, or the environment and constraints?'
}];
const MOCK_OUTPUT: OutputData = {
  mentalModels: ['Leverage', 'Specific Knowledge', 'Long-term Thinking', 'Asymmetric Upside'],
  reframeText: "You're not choosing between security and risk—you're choosing between two types of risk. Staying carries the risk of regret and missed compounding of your specific knowledge. Leaving carries financial risk but with asymmetric upside. The real question is: which risk aligns with who you want to become?",
  quote: {
    text: 'Seek wealth, not money or status. Wealth is having assets that earn while you sleep.',
    source: 'Naval Ravikant, How to Get Rich (without getting lucky)'
  },
  socraticQuestions: ["If you knew you couldn't fail, which path would you choose?", 'What would you need to believe about yourself to make the leap feel obvious?', 'In 10 years, which decision would you regret more—trying and failing, or never trying?', 'Can you create a smaller experiment to test your idea before fully committing?', "What's the minimum viable version of your new venture that you could start today, while employed?"]
};
export function NavalLensPage() {
  const [step, setStep] = useState<Step>('input');
  const [userInput, setUserInput] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showClarification, setShowClarification] = useState(true); // Toggle for demo
  const handleInputSubmit = useCallback((input: string) => {
    setUserInput(input);
    setStep('loading');
    // Simulate API call
    setTimeout(() => {
      // For prototype: randomly show clarification or go straight to output
      if (showClarification) {
        setStep('clarification');
        setShowClarification(false); // Only show once per session for demo
      } else {
        setStep('output');
      }
    }, 2500);
  }, [showClarification]);
  const handleClarificationSubmit = useCallback((_answers: Record<string, string>) => {
    setStep('loading');
    // Simulate processing with answers
    setTimeout(() => {
      setStep('output');
    }, 1500);
  }, []);
  const handleClarificationSkip = useCallback(() => {
    setStep('loading');
    setTimeout(() => {
      setStep('output');
    }, 1500);
  }, []);
  const handleEdit = useCallback(() => {
    setStep('input');
  }, []);
  const handleStartFresh = useCallback(() => {
    setUserInput('');
    setStep('input');
    setShowClarification(true);
  }, []);
  const handleLoadingTimeout = useCallback(() => {
    // For prototype, we'll just continue after timeout
    // In production, this would show an error
  }, []);
  const handleRetry = useCallback(() => {
    setErrorMessage('');
    setStep('loading');
    setTimeout(() => {
      setStep('output');
    }, 2000);
  }, []);
  return <main className="min-h-screen w-full bg-background" data-id="element-69">
      <div className="container max-w-4xl mx-auto px-4 py-12 md:py-20" data-id="element-70">
        {/* Header - only show on input step */}
        {step !== 'input' && <div className="mb-8" data-id="element-71">
            <button type="button" onClick={handleStartFresh} className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1" data-id="element-72">
              ← Back to start
            </button>
          </div>}

        {/* Step Content */}
        {step === 'input' && <InputStep onSubmit={handleInputSubmit} initialValue={userInput} data-id="element-73" />}

        {step === 'loading' && <LoadingStep onTimeout={handleLoadingTimeout} data-id="element-74" />}

        {step === 'clarification' && <ClarificationStep questions={MOCK_CLARIFICATION_QUESTIONS} onSubmit={handleClarificationSubmit} onSkip={handleClarificationSkip} data-id="element-75" />}

        {step === 'output' && <OutputStep data={MOCK_OUTPUT} originalInput={userInput} onEdit={handleEdit} onStartFresh={handleStartFresh} data-id="element-76" />}

        {step === 'error' && <div className="w-full max-w-2xl mx-auto text-center py-16" data-id="element-77">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-destructive/10 mb-4" data-id="element-78">
              <AlertCircleIcon className="w-6 h-6 text-destructive" data-id="element-79" />
            </div>
            <h2 className="text-xl font-semibold text-foreground mb-2" data-id="element-80">
              Something went wrong
            </h2>
            <p className="text-muted-foreground mb-6" data-id="element-81">
              {errorMessage || "We couldn't process your request. Please try again."}
            </p>
            <button type="button" onClick={handleRetry} className="py-3 px-6 rounded-lg font-medium text-base bg-primary text-primary-foreground hover:opacity-90 active:scale-[0.98] transition-all duration-200 inline-flex items-center justify-center gap-2" data-id="element-82">
              <RefreshCwIcon className="w-4 h-4" data-id="element-83" />
              Try Again
            </button>
          </div>}

        {/* Footer */}
        <footer className="mt-16 pt-8 border-t border-border text-center" data-id="element-84">
          <p className="text-sm text-muted-foreground" data-id="element-85">
            Inspired by the wisdom of{' '}
            <a href="https://nav.al" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-foreground transition-colors" data-id="element-86">
              Naval Ravikant
            </a>
          </p>
        </footer>
      </div>
    </main>;
}